import { useState, useEffect, useCallback, useRef } from 'react';
import { useAgentStream } from '../../../hooks/useAgentStream';
import { parseAgentMessage } from '../../../utils/parsers';
import type { WebSocketMessage } from '../../../services/websocket.service';
import type { AgentStreamState, AgentMessagePart } from '../../../types/agent-messages';
import type { AgentChatMessage } from './types';
import type { UseWebSocketConnectionReturn } from './useWebSocketConnection';

export interface UseAgentWebSocketOptions {
  profileId: string;
  sessionIdRef: React.MutableRefObject<string | null>;
  connection: UseWebSocketConnectionReturn;
}

export interface UseAgentWebSocketReturn {
  messages: AgentChatMessage[];
  streamState: AgentStreamState;
  isStreaming: boolean;
  isWaiting: boolean;
  error: string | null;
  sendMessage: (text: string) => void;
  sendStructured: (payload: Record<string, unknown>) => void;
  clearMessages: () => void;
  setMessages: React.Dispatch<React.SetStateAction<AgentChatMessage[]>>;
}

const HANDLER_ID = 'agentChat';

export const useAgentWebSocket = ({
  profileId,
  sessionIdRef,
  connection,
}: UseAgentWebSocketOptions): UseAgentWebSocketReturn => {
  const [messages, setMessagesRaw] = useState<AgentChatMessage[]>([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wrap setMessages with a global dedup guard: never allow two consecutive
  // assistant messages with the same content.  This catches every code-path
  // regardless of how the duplicate is produced.
  const setMessages: React.Dispatch<React.SetStateAction<AgentChatMessage[]>> = useCallback(
    (action) => {
      setMessagesRaw(prev => {
        const next = typeof action === 'function' ? action(prev) : action;
        // If the updater returned the same reference, nothing changed.
        if (next === prev) return prev;
        // Dedup: if the new last message is an assistant message whose content
        // matches the previous last assistant message, drop it.
        if (next.length > prev.length && next.length >= 2) {
          const newLast = next[next.length - 1];
          const prevLast = next[next.length - 2];
          if (
            newLast.role === 'assistant' &&
            prevLast.role === 'assistant' &&
            newLast.content === prevLast.content
          ) {
            return prev;
          }
        }
        return next;
      });
    },
    [],
  );

  const {
    state: streamState,
    handleEvent,
    reset: resetStream,
    buildMessageParts,
    setStructuredParts,
  } = useAgentStream();

  const liveMsgIdRef = useRef<string | null>(null);
  const finalizingRef = useRef(false);

  const handleEventRef = useRef(handleEvent);
  handleEventRef.current = handleEvent;
  const streamStateRef = useRef(streamState);
  streamStateRef.current = streamState;
  const buildMessagePartsRef = useRef(buildMessageParts);
  buildMessagePartsRef.current = buildMessageParts;
  const resetStreamRef = useRef(resetStream);
  resetStreamRef.current = resetStream;
  const setStructuredPartsRef = useRef(setStructuredParts);
  setStructuredPartsRef.current = setStructuredParts;

  const finalizeStream = useCallback(() => {
    // Double-entry guard: prevent concurrent / StrictMode double-fire
    if (finalizingRef.current) return;
    if (!liveMsgIdRef.current) return;

    finalizingRef.current = true;
    const msgId = liveMsgIdRef.current;
    liveMsgIdRef.current = null;

    const ss = streamStateRef.current;
    const parts = buildMessagePartsRef.current();
    const text = ss.fullMessage ?? ss.streamedText;
    const toolCount = ss.toolCalls?.length ?? ss.toolSteps.length;

    setMessages(prev => {
      // Skip if a message with this id already exists
      if (prev.some(m => m.id === msgId)) return prev;
      // Skip if the last assistant message has identical content
      if (prev.length > 0) {
        const last = prev[prev.length - 1];
        if (last.role === 'assistant' && last.content === text) return prev;
      }
      return [
        ...prev,
        {
          id: msgId,
          role: 'assistant' as const,
          content: text,
          timestamp: new Date(),
          agentParts: parts.length > 0 ? parts : undefined,
          toolCallCount: toolCount > 0 ? toolCount : undefined,
        },
      ];
    });

    setIsWaiting(false);
    resetStream();
    // Release the lock after a tick so any queued calls are still blocked
    queueMicrotask(() => { finalizingRef.current = false; });
  }, [resetStream]);

  useEffect(() => {
    if (streamState.phase === 'complete' || streamState.phase === 'error') {
      finalizeStream();
    }
  }, [streamState.phase, finalizeStream]);

  // Register message handler with the shared connection
  useEffect(() => {
    const handler = (data: WebSocketMessage) => {
      const agentTypes = new Set([
        'agentStart', 'thinking', 'toolUse', 'toolResult', 'delta', 'agentEnd',
      ]);

      // Thread-management types are handled by useThreadManager
      const threadTypes = new Set(['threads', 'threadCreated', 'history', 'session_reset']);
      if (threadTypes.has(data.type!)) return;

      if (data.type === 'typing') {
        setIsWaiting(true);
        if (!liveMsgIdRef.current) {
          liveMsgIdRef.current = `assistant-${Date.now()}`;
        }
        return;
      }

      if (data.type === 'agentStart') {
        setIsWaiting(false);
        if (!liveMsgIdRef.current) {
          liveMsgIdRef.current = `assistant-${Date.now()}`;
        }
      }

      if (agentTypes.has(data.type!)) {
        handleEventRef.current(data);
        return;
      }

      if (data.type === 'message' && data.source === 'agent') {
        setIsWaiting(false);
        if (data.structuredParts) {
          setStructuredPartsRef.current(
            data.structuredParts as AgentMessagePart[],
          );
        }
        // Always forward the message event to handleEvent so the stream
        // phase transitions to 'complete'. The finalizeStream() callback
        // (triggered by the phase change) will use the streamed delta
        // content when available, so no duplicate bubble is created.
        handleEventRef.current(data);
        return;
      }

      if (data.type === 'message') {
        setIsWaiting(false);
        const content = data.message ?? '';
        if (content && liveMsgIdRef.current) {
          const toolSteps = streamStateRef.current.toolSteps;
          const parts = parseAgentMessage({
            text: content,
            toolSteps: [...toolSteps],
            toolCalls: null,
            fullMessage: content,
          });
          const msgId = liveMsgIdRef.current!;
          liveMsgIdRef.current = null;
          setMessages(prev => {
            // Dedup: skip if id already exists or last assistant msg has same content
            if (prev.some(m => m.id === msgId)) return prev;
            if (prev.length > 0) {
              const last = prev[prev.length - 1];
              if (last.role === 'assistant' && last.content === content) return prev;
            }
            return [
              ...prev,
              {
                id: msgId,
                role: 'assistant' as const,
                content,
                timestamp: new Date(),
                agentParts: parts.length > 0 ? parts : undefined,
              },
            ];
          });
          resetStreamRef.current();
        }
        return;
      }

      if (data.type === 'error') {
        handleEventRef.current(data);
        return;
      }

      if (!data.type && data.message) {
        console.warn('[V2] Backend error:', data.message);
        setIsWaiting(false);
        setError(String(data.message));
        if (liveMsgIdRef.current) {
          setMessages(prev => [
            ...prev,
            {
              id: liveMsgIdRef.current!,
              role: 'assistant',
              content: `Error: ${data.message}`,
              timestamp: new Date(),
            },
          ]);
          liveMsgIdRef.current = null;
          resetStreamRef.current();
        }
        return;
      }

      if (data.type && !agentTypes.has(data.type)) {
        console.warn('[V2] Unhandled WS message type:', data.type);
      }
    };

    connection.addMessageHandler(HANDLER_ID, handler);
    return () => connection.removeMessageHandler(HANDLER_ID);
  }, [connection.addMessageHandler, connection.removeMessageHandler]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      setMessages(prev => [
        ...prev,
        {
          id: `user-${Date.now()}`,
          role: 'user',
          content: trimmed,
          timestamp: new Date(),
        },
      ]);

      setError(null);
      resetStream();

      const payload: Record<string, unknown> = {
        action: 'sendMessage',
        message: trimmed,
        profileId,
      };
      if (sessionIdRef.current) {
        payload.sessionId = sessionIdRef.current;
      }
      connection.sendRaw(payload);
    },
    [resetStream, connection.sendRaw, profileId, sessionIdRef],
  );

  const sendStructured = useCallback(
    (payload: Record<string, unknown>) => {
      setError(null);
      resetStream();
      const full: Record<string, unknown> = {
        action: 'sendMessage',
        ...payload,
        profileId,
      };
      if (sessionIdRef.current) {
        full.sessionId = sessionIdRef.current;
      }
      connection.sendRaw(full);
    },
    [resetStream, connection.sendRaw, profileId, sessionIdRef],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    resetStream();
    liveMsgIdRef.current = null;
    finalizingRef.current = false;
    setIsWaiting(false);
    setError(null);
  }, [resetStream]);

  return {
    messages,
    streamState,
    isStreaming: streamState.phase !== 'idle' && streamState.phase !== 'complete' && streamState.phase !== 'error',
    isWaiting,
    error,
    sendMessage,
    sendStructured,
    clearMessages,
    setMessages,
  };
};
